import React from "react";
import PropTypes from "prop-types";
import styles from "./EMPRadioCardGroup.module.scss";

const NO_DATA = "--";

const formatValue = (value) =>
  value != null && String(value).trim() !== "" ? String(value).trim() : NO_DATA;

const EMPRadioCardGroup = ({
  name,
  value,
  onChange,
  options = [],
  disabled = false,
  className = "",
  ariaLabel,
  testId,
  selectedColor,
}) => {
  const handleSelect = (optionValue, optionDisabled) => {
    if (!disabled && !optionDisabled && onChange) {
      onChange(optionValue);
    }
  };

  return (
    <div
      className={`${styles.cardGroup} ${className}`.trim()}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map((option, index) => {
        const isSelected = value === option.value;
        const optionDisabled = disabled || Boolean(option.disabled);
        const radioId = `${name}-${index}`;
        const optionTestId = testId ? `${testId}-${option.value}` : undefined;
        const hasTable = Array.isArray(option.table) && option.table.length > 0;

        return (
          <label
            key={option.value}
            htmlFor={radioId}
            className={`${styles.card} ${
              isSelected ? styles.cardSelected : ""
            } ${optionDisabled ? styles.cardDisabled : ""}`.trim()}
            style={
              isSelected && selectedColor
                ? {
                    borderColor: selectedColor,
                    boxShadow: `0 0 0 1px ${selectedColor}`,
                  }
                : undefined
            }
            data-testid={optionTestId}
          >
            <div className={styles.cardHeader}>
              <span className={styles.radioControl}>
                <input
                  id={radioId}
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => handleSelect(option.value, optionDisabled)}
                  disabled={optionDisabled}
                  className={styles.radioInput}
                  data-testid={
                    optionTestId ? `${optionTestId}-input` : undefined
                  }
                />
                <span
                  className={`${styles.radioOuter} ${
                    isSelected ? styles.radioOuterSelected : ""
                  }`.trim()}
                  style={
                    isSelected && selectedColor
                      ? { borderColor: selectedColor }
                      : undefined
                  }
                >
                  {isSelected && (
                    <span
                      className={styles.radioInner}
                      style={
                        selectedColor
                          ? { backgroundColor: selectedColor }
                          : undefined
                      }
                    />
                  )}
                </span>
              </span>

              <span className={styles.headerText}>
                <span className={styles.title}>{option.label}</span>
                {option.secondaryText && (
                  <span
                    className={styles.secondaryText}
                    data-testid={
                      optionTestId
                        ? `${optionTestId}-secondary-text`
                        : undefined
                    }
                  >
                    {option.secondaryText}
                  </span>
                )}
              </span>
            </div>

            {hasTable && (
              <div
                className={styles.table}
                data-testid={optionTestId ? `${optionTestId}-table` : undefined}
              >
                {option.table.map((column) => (
                  <div key={column.label} className={styles.tableItem}>
                    <span className={styles.tableLabel}>{column.label}</span>
                    <span className={styles.tableValue}>
                      {formatValue(column.value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </label>
        );
      })}
    </div>
  );
};

EMPRadioCardGroup.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
      secondaryText: PropTypes.string,
      disabled: PropTypes.bool,
      table: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
      ),
    })
  ).isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  testId: PropTypes.string,
  selectedColor: PropTypes.string,
};

export default EMPRadioCardGroup;
